/**
 * Conversation Detailed Report — column model.
 *
 * Unlike Agent Performance, this catalog is NOT a constant: the tenant's
 * additional properties are user-defined, so the API discovers them at query
 * time (`additionalPropertiesColumns`) and they are appended to the fixed
 * columns here. Everything downstream — the chooser, the saved view, the
 * export — therefore takes a catalog as an argument rather than importing one.
 *
 * Reference ID is the pinned identity column, matching the inbox's
 * Conversation ID: with ~40 columns plus however many custom properties a
 * tenant has defined, a row whose identity has scrolled away is unreadable.
 */
import type { ReportColumnDef } from '../agent-performance/columns.js'

export const CONVERSATION_DETAIL_REPORT_ID = 'conversation.detail'

/** Prefix for a dynamic column's key, so it can never collide with a fixed one. */
export const ADDITIONAL_PROPERTY_PREFIX = 'ap__'

/**
 * The fixed columns, in report order: identity, then the customer and the
 * organisational placement, then the classification catalogs, then the SLA and
 * timing facts. Additional properties are appended after all of these.
 */
export const CONVERSATION_DETAIL_COLUMNS: ReportColumnDef[] = [
  { key: 'referenceId', label: 'Reference ID', width: 200, stickyLeft: 0, locked: true },
  { key: 'subject', label: 'Subject', width: 320 },
  { key: 'channelOrigin', label: 'Channel', width: 150 },
  { key: 'customerName', label: 'Client', width: 220 },
  { key: 'customerEmail', label: 'Client Email', width: 240 },
  { key: 'agentName', label: 'Agent', width: 200 },
  { key: 'teamName', label: 'Team', width: 180 },
  { key: 'campaignName', label: 'Campaign', width: 200 },
  { key: 'locationName', label: 'Location', width: 180 },
  { key: 'branchName', label: 'Branch', width: 180 },
  { key: 'departmentName', label: 'Department', width: 180 },
  { key: 'status', label: 'Status', width: 160 },
  { key: 'state', label: 'State', width: 160 },
  { key: 'type', label: 'Type', width: 160 },
  { key: 'category', label: 'Category', width: 180 },
  { key: 'subCategory', label: 'Sub Category', width: 180 },
  { key: 'priority', label: 'Priority', width: 150 },
  { key: 'complexity', label: 'Complexity', width: 150 },
  { key: 'urgency', label: 'Urgency', width: 150 },
  { key: 'impact', label: 'Impact', width: 150 },
  { key: 'openedAt', label: 'Created', width: 200 },
  { key: 'lastMessageAt', label: 'Last Message', width: 200 },
  { key: 'firstRespondedAt', label: 'First Responded', width: 200 },
  { key: 'resolvedAt', label: 'Resolved', width: 200 },
  { key: 'closedAt', label: 'Closed', width: 200 },
  { key: 'expectedResponseTimeSec', label: 'Expected Response', width: 200, align: 'right' },
  { key: 'actualResponseTimeSec', label: 'Actual Response', width: 200, align: 'right' },
  { key: 'expectedResolutionTimeSec', label: 'Expected Resolution', width: 220, align: 'right' },
  { key: 'actualResolutionTimeSec', label: 'Actual Resolution', width: 220, align: 'right' },
  { key: 'slaBreached', label: 'SLA Breached', width: 160, align: 'center' },
  { key: 'olaBreached', label: 'OLA Breached', width: 160, align: 'center' },
  { key: 'csatScore', label: 'CSAT', width: 120, align: 'right' },
  { key: 'approvalStatus', label: 'Approval Status', width: 180 },
  { key: 'approvalLevel', label: 'Approval Level', width: 160, align: 'right' },
  { key: 'tags', label: 'Tags', width: 220 },
]

export interface AdditionalPropertyColumn {
  key: string
  label: string
  dataType: string
}

/**
 * Fixed columns followed by the tenant's additional properties.
 *
 * Dynamic keys are namespaced (`ap__<key>`) so a property called "status"
 * cannot shadow the conversation's own Status column — both in the table and
 * in a saved view, where the two would otherwise be indistinguishable.
 */
export function buildDetailColumns(
  additional: AdditionalPropertyColumn[],
): ReportColumnDef[] {
  return [
    ...CONVERSATION_DETAIL_COLUMNS,
    ...additional.map((a): ReportColumnDef => ({
      key: `${ADDITIONAL_PROPERTY_PREFIX}${a.key}`,
      label: a.label || a.key,
      width: 200,
      align: a.dataType === 'integer' || a.dataType === 'numeric' ? 'right' : 'left',
    })),
  ]
}

/**
 * Read a cell.
 *
 * Additional properties arrive already namespaced — the API aliases them as
 * `ap__<name>` directly on the row — so the key is a plain lookup. An earlier
 * version reached into a nested `additionalProperties` object that this
 * endpoint never returns, which is why every custom column rendered blank.
 */
export function readDetailCell(
  row: Record<string, unknown>,
  key: string,
): unknown {
  return row[key] ?? null
}

/**
 * Reconcile a saved view against a catalog that is only known at runtime.
 *
 * The Agent Performance version closes over a module constant; this one takes
 * the catalog, because a tenant can add or remove an additional property at
 * any time. A view referencing a property that has since been deleted drops
 * that key rather than rendering an empty column forever.
 */
export function sanitizeDetailColumns(
  saved: unknown,
  catalog: ReportColumnDef[],
): string[] {
  const allKeys = catalog.map((c) => c.key)
  if (!Array.isArray(saved)) return allKeys

  const known = new Set(allKeys)
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const key of saved) {
    if (typeof key !== 'string' || !known.has(key) || seen.has(key)) continue
    seen.add(key)
    ordered.push(key)
  }
  for (const c of catalog) {
    if (c.locked && !seen.has(c.key)) {
      seen.add(c.key)
      ordered.unshift(c.key)
    }
  }
  return ordered.length > 0 ? ordered : allKeys
}
